<template>
    <div style="position: relative;">
        <top-bar-secondary>
            <template v-slot:module-action-left>
                <b-navbar-nav v-if="isEditMode && !isSavingEdit">
                    <b-nav-item @click="saveEdit">
                        <i class="cus-disk"></i>Save + Done
                    </b-nav-item>
                    <b-nav-item @click="cancelEdit">
                        <i class="cus-cancel"></i>Cancel
                    </b-nav-item>
                </b-navbar-nav>
                <b-navbar-nav v-else-if="isSavingEdit">
                    <b-nav-item>
                        <i class="cus-hourglass"></i>Saving...
                    </b-nav-item>
                </b-navbar-nav>
                <b-navbar-nav v-else>
                    <b-nav-item
                        v-if="_hasAccess('inventory.purchase.order.edit')"
                        @click="editOrder"
                    >
                        <i class="cus-page-white-edit"></i>Edit Order
                    </b-nav-item>
                    <b-nav-item
                        v-if="
                            _hasAccess('inventory.purchase.order.submit') &&
                            hasItems &&
                            isStatusOpen
                        "
                        @click="submitOrder"
                    >
                        <i class="cus-page-white-go"></i>Submit Order
                    </b-nav-item>
                    <b-nav-item-dropdown>
                        <template slot="button-content">
                            <i class="cus-page-attach"></i>
                            <b>Export</b>
                        </template>
                        <b-dropdown-item @click="emailOrder">
                            <i class="cus-email"></i>Email Order
                        </b-dropdown-item>
                        <b-dropdown-item @click="printOrder">
                            <i class="cus-printer"></i>Print Order
                        </b-dropdown-item>
                    </b-nav-item-dropdown>
                    <b-nav-item
                        v-if="
                            isStatusOpen &&
                            _hasAccess('inventory.purchase.order.delete')
                        "
                        @click="$refs.modalDelete.show()"
                    >
                        <i class="cus-delete"></i>Delete Order
                    </b-nav-item>
                    <b-nav-item @click="saveAsTemplate">
                        <i class="cus-layout-add"></i>Save As Template
                    </b-nav-item>
                </b-navbar-nav>
            </template>
        </top-bar-secondary>

        <content-wrapper>
            <router-view></router-view>
        </content-wrapper>

        <b-modal
            no-fade
            ref="modalDelete"
            title-tag="h2"
            title="Delete Order"
            lazy
            @ok="proceedDelete"
        >
            Are you sure you want to permanently delete this order?
            <template v-slot:modal-footer="{ cancel }">
                <loader v-if="isDeleting" :margin="1" />

                <b-button
                    class="mr-auto"
                    variant="secondary"
                    @click="cancel"
                    v-if="!isDeleting"
                >
                    Cancel
                </b-button>

                <b-button
                    v-if="!isDeleting"
                    variant="primary"
                    @click="proceedDelete"
                >
                    Delete
                </b-button>
            </template>
        </b-modal>

        <ModalSubmit ref="modalSubmit" @on-success="refetchOrderDetails" />
        <ModalEmailOrder ref="modalEmailOrder" />
        <ModalSaveAsTemplate ref="modalSaveAsTemplate" />
    </div>
</template>

<script>
import Loader from "@/module/commons/loader";
import { DateTime } from "luxon";
import ModalSubmit from "@/components/inventory-purchase-order/modal-submit";
import ModalEmailOrder from "@/components/inventory-purchase-order/modal-email-order";
import ModalSaveAsTemplate from "@/components/inventory-purchase-order/modal-save-as-template";
import TopBarSecondary from "@/module/layout/top-bar-secondary.vue";
import ContentWrapper from "@/module/layout/content-wrapper.vue";

export default {
    components: {
        Loader,
        ModalSubmit,
        ModalEmailOrder,
        ModalSaveAsTemplate,
        TopBarSecondary,
        ContentWrapper
    },
    data() {
        return {
            isDeleting: false
        };
    },
    computed: {
        hasItems: {
            get() {
                let items = this.$store.state.inventoryOrder.items;
                if (items.length > 0) {
                    return true;
                }
                return false;
            }
        },
        items: {
            get() {
                return this.$store.state.inventoryOrder.items;
            }
        },
        orderId: {
            get() {
                return this.$store.state.inventoryOrder.orderId;
            }
        },
        sourceCatalog: {
            get() {
                return this.$store.state.inventoryOrder.sourceCatalog;
            }
        },
        orderIndex: {
            get() {
                return this.$store.state.inventoryOrder.orderIndex;
            }
        },
        supplier: {
            get() {
                return this.$store.state.inventoryOrder.supplier;
            }
        },
        isEditMode: {
            get() {
                return this.$store.state.inventoryOrder.isEditMode;
            }
        },
        isSavingEdit: {
            get() {
                return this.$store.state.inventoryOrder.isSavingEdit;
            }
        },
        isStatusOpen: {
            get() {
                return this.$store.state.inventoryOrder.details.status === 1;
            }
        },
        editDetails: {
            get() {
                return this.$store.state.inventoryOrder.editDetails;
            }
        }
    },
    mounted() {
        this.storeAudit(this.$route.params.id);
    },
    methods: {
        refetchOrderDetails() {
            try {
                this.$store.dispatch("inventoryOrder/fetchDetails", {
                    orderId: this.$route.params.id
                });
            } catch (e) {
                this._message(e.message, "danger");
            }
        },
        submitOrder() {
            this.$refs.modalSubmit.open(this.orderId);
        },
        editOrder() {
            this.$store.dispatch("inventoryOrder/editOrder");
        },
        cancelEdit() {
            this.$store.commit("inventoryOrder/cancelEdit");
        },
        async saveEdit() {
            try {
                // handle 'date'. in order to use a function from helpers,
                // we need to do that here, instead in vuex files.
                // vuex - is not a component, so it can't use whatever helper functions (mixin).

                let currentDateValue = this.editDetails.date;
                let dateUnix;

                if (typeof currentDateValue === "string") {
                    dateUnix = DateTime.fromFormat(
                        currentDateValue,
                        this._dateFormat,
                        { zone: this._timeZone }
                    ).toSeconds();
                } else if (typeof currentDateValue === "object") {
                    dateUnix = this._fromDateCarbon(
                        currentDateValue
                    ).toSeconds();
                }

                await this.$store.dispatch("inventoryOrder/saveEdit", {
                    dateUnix: dateUnix
                });

                this._message("Details have been updated");
            } catch (e) {
                this._message(e.message, "danger");
            }
        },
        async proceedDelete() {
            try {
                this.isDeleting = true;

                let res = await this.$http.delete(
                    `${process.env.BARRAMUNDI}inventory/order/${this.orderId}`
                );

                if (
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code === 200
                ) {
                    this._message("Order deleted");

                    setTimeout(() => {
                        this.$router.push({
                            name: "inventory_dashboard"
                        });
                    }, 500);
                } else {
                    throw new Error("Unable to delete order");
                }
            } catch (e) {
                this.isDeleting = false;
                this._message(e.message, "danger");
            }
        },
        emailOrder() {
            this.$refs.modalEmailOrder.open({
                orderId: this.orderId,
                orderIndex: this.orderIndex,
                businessName: this._setting.business.name,
                businessPhone: this._setting.business.phone,
                supplierId: this.supplier.id,
                supplierName: this.supplier.name,
                supplierEmail: this.supplier.email
            });
        },
        async printOrder() {
            try {
                document.body.style.cursor = "wait";
                this._message("Generating PDF, please wait....", "info");

                let res = await this.$http.post(
                    `${process.env.BARRAMUNDI}inventory/order/print-pdf`,
                    {
                        purchase_order_id: this.orderId
                    }
                );

                document.body.style.cursor = "";

                if (
                    res.data.response &&
                    res.data.response.resources &&
                    res.data.response.resources.path
                ) {
                    this._message("Opening...", "info");
                    window.open(res.data.response.resources.path, "_blank");
                } else {
                    throw new Error("Error: Unable to generate PDF");
                }
            } catch (error) {
                this._message(error.message, "danger");
            }
        },
        saveAsTemplate() {
            let elements = [];
            let isIntegratedSupplier = this.sourceCatalog && this.sourceCatalog !== ""; // prettier-ignore

            for (let item of this.items) {
                elements.push({
                    item_id: isIntegratedSupplier ? item.source_product_id : item.item_id, // prettier-ignore
                    quantity: item.quantity_ordered || 0
                });
            }

            this.$refs.modalSaveAsTemplate.open({
                title: `${this.supplier.name} Order #${this.orderIndex}`,
                sourceCatalog: this.sourceCatalog,
                supplierId: this.supplier.id,
                elements
            });
        },
        async storeAudit(purchaseOrder) {
            const fullPath = _.clone(this.$router.currentRoute.fullPath);

            if (!purchaseOrder) {
                return;
            }

            await this._getSetting();
            await this._saveAudit({
                action: "view",
                path: "/inventory/purchase_order/view." + purchaseOrder,
                reference_id: purchaseOrder,
                fullPath
            });
        }
    }
};
</script>
